import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical 66-book Protestant chapter counts. Used by preloadNextChapter to
// decide when to advance to the next book or stop at the end of Revelation.
const BIBLE_CHAPTER_COUNTS = {
  Genesis: 50, Exodus: 40, Leviticus: 27, Numbers: 36, Deuteronomy: 34,
  Joshua: 24, Judges: 21, Ruth: 4,
  '1 Samuel': 31, '2 Samuel': 24, '1 Kings': 22, '2 Kings': 25,
  '1 Chronicles': 29, '2 Chronicles': 36, Ezra: 10, Nehemiah: 13, Esther: 10,
  Job: 42, Psalms: 150, Proverbs: 31, Ecclesiastes: 12,
  'Song of Solomon': 8, Isaiah: 66, Jeremiah: 52, Lamentations: 5, Ezekiel: 48,
  Daniel: 12, Hosea: 14, Joel: 3, Amos: 9, Obadiah: 1, Jonah: 4, Micah: 7,
  Nahum: 3, Habakkuk: 3, Zephaniah: 3, Haggai: 2, Zechariah: 14, Malachi: 4,
  Matthew: 28, Mark: 16, Luke: 24, John: 21, Acts: 28, Romans: 16,
  '1 Corinthians': 16, '2 Corinthians': 13, Galatians: 6, Ephesians: 6,
  Philippians: 4, Colossians: 4, '1 Thessalonians': 5, '2 Thessalonians': 3,
  '1 Timothy': 6, '2 Timothy': 4, Titus: 3, Philemon: 1, Hebrews: 13,
  James: 5, '1 Peter': 5, '2 Peter': 3, '1 John': 5, '2 John': 1, '3 John': 1,
  Jude: 1, Revelation: 22,
};
const BIBLE_BOOK_ORDER = Object.keys(BIBLE_CHAPTER_COUNTS);

// Bible API calls
export const getVerse = async (reference, translation = 'KJV') => {
  try {
    const response = await axios.get(`${API}/bible/verse`, {
      params: { reference, translation }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching verse:', error);
    throw error;
  }
};

export const searchVerses = async (query, translation = 'KJV', limit = 20) => {
  try {
    const response = await axios.get(`${API}/bible/search`, {
      params: { query, translation, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching verses:', error);
    throw error;
  }
};

export const getParallelTranslations = async (reference, translations = ['KJV', 'ESV', 'NIV', 'NLT']) => {
  try {
    const response = await axios.get(`${API}/bible/parallel`, {
      params: { 
        reference, 
        translations: translations.join(',')
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching parallel translations:', error);
    throw error;
  }
};

export const getTranslations = async () => {
  try {
    const response = await axios.get(`${API}/bible/translations`);
    return response.data;
  } catch (error) {
    console.error('Fetch verse error:', error);
    throw error;
  }
};

import { getOfflineChapter } from '../utils/offlineBibleStore';

export const getChapter = async (book, chapter, translation = 'KJV') => {
  // Offline-first: if the user has downloaded this translation, serve the
  // chapter from IndexedDB and skip the network entirely.
  try {
    const offline = await getOfflineChapter(translation, book, chapter);
    if (offline && offline.length) {
      return {
        book,
        chapter,
        translation,
        verses: offline,
        total_verses: offline.length,
        cached_verses: offline.length,
        fetched_verses: 0,
        source: 'offline',
      };
    }
  } catch (err) {
    // Fall through to network on any IDB error
    console.warn('offline chapter lookup failed, falling back to network', err);
  }

  try {
    const params = new URLSearchParams({ book, chapter, translation });
    // Hard timeout so a stalled request can never leave the reader spinning
    // on "Loading chapter…" indefinitely — it aborts and surfaces an error the
    // caller can recover from (keep previous verses / retry).
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);
    let response;
    try {
      response = await fetch(`${API}/bible/chapter?${params}`, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      console.error('Chapter fetch failed:', response.status, response.statusText);
      throw new Error(`Failed to fetch chapter: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching chapter:', error);
    throw error;
  }
};

/**
 * Return the "natural next" {book, chapter} for the given position, or null
 * if we've hit the end of Revelation.
 */
export const getNextChapter = (book, chapter) => {
  const total = BIBLE_CHAPTER_COUNTS[book];
  if (!total) return null;
  if (chapter < total) return { book, chapter: chapter + 1 };
  const idx = BIBLE_BOOK_ORDER.indexOf(book);
  if (idx === -1 || idx >= BIBLE_BOOK_ORDER.length - 1) return null;
  return { book: BIBLE_BOOK_ORDER[idx + 1], chapter: 1 };
};

// In-flight / recently-preloaded set so we never fire the same preload twice.
const _preloadedKeys = new Set();

/**
 * Fire-and-forget background fetch of the chapter following (book, chapter).
 * - Uses getChapter() which is already offline-first, so if the translation
 *   is downloaded it short-circuits to IDB with no network hit.
 * - The service worker's runtime-cache strategy stores any successful
 *   /api/bible/chapter response, so the next navigation hits warm cache.
 * - De-duped: a given (tr, book, chapter) is only preloaded once per session.
 * - Throttled to requestIdleCallback when available so it never competes with
 *   the main render.
 */
export const preloadNextChapter = (currentBook, currentChapter, translation = 'KJV') => {
  if (typeof window === 'undefined') return;
  const next = getNextChapter(currentBook, currentChapter);
  if (!next) return;
  const key = `${translation}::${next.book}::${next.chapter}`;
  if (_preloadedKeys.has(key)) return;
  _preloadedKeys.add(key);

  const run = () => {
    getChapter(next.book, next.chapter, translation).catch((err) => {
      _preloadedKeys.delete(key);
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[preload] skipped', key, err?.message);
      }
    });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 500);
  }
};

/**
 * Parse a loose verse reference string like "Romans 8:1-4" or "1 John 4:8"
 * into a {book, chapter} tuple. Returns null on failure. Accepts numeric
 * book prefixes ("1 John", "2 Corinthians", "3 John") and multi-word books
 * ("Song of Solomon"). Matches the canonical book names we use internally.
 */
export const parseReference = (ref) => {
  if (!ref || typeof ref !== 'string') return null;
  // Common alias -> canonical. Keep lowercase.
  const BOOK_ALIASES = {
    'psalm': 'Psalms',
    'song of songs': 'Song of Solomon',
    'canticles': 'Song of Solomon',
    'ecclesiasticus': null, // deuterocanonical, not in our canon
    'apocalypse': 'Revelation',
  };
  const lower = ref.trim().toLowerCase();

  // Try aliases first (longest match wins)
  const aliasEntries = Object.entries(BOOK_ALIASES).sort((a, b) => b[0].length - a[0].length);
  for (const [alias, canonical] of aliasEntries) {
    if (canonical && lower.startsWith(alias)) {
      const rest = lower.slice(alias.length).trim();
      const m = rest.match(/^(\d+)/);
      if (m) {
        const chapter = parseInt(m[1], 10);
        if (Number.isFinite(chapter) && chapter >= 1) {
          return { book: canonical, chapter };
        }
      }
    }
  }

  // Canonical book list; longest match wins ("Song of Solomon" before "Song").
  const bookLens = [...BIBLE_BOOK_ORDER].sort((a, b) => b.length - a.length);
  for (const book of bookLens) {
    const bookLower = book.toLowerCase();
    if (lower.startsWith(bookLower)) {
      const rest = lower.slice(bookLower.length).trim();
      const m = rest.match(/^(\d+)/);
      if (m) {
        const chapter = parseInt(m[1], 10);
        if (Number.isFinite(chapter) && chapter >= 1) {
          return { book, chapter };
        }
      }
    }
  }
  return null;
};

/**
 * Context-aware preload: given any list of loose verse reference strings
 * (e.g. ["Romans 8:1", "Psalm 23:1-6"]), extract unique (book, chapter)
 * tuples and fire idle-time fetches for each. Same de-duping + offline-first
 * guarantees as preloadNextChapter.
 */
export const preloadReferences = (references, translation = 'KJV') => {
  if (typeof window === 'undefined') return;
  if (!Array.isArray(references)) return;
  const seen = new Set();
  for (const raw of references) {
    const parsed = parseReference(raw);
    if (!parsed) continue;
    const k = `${parsed.book}::${parsed.chapter}`;
    if (seen.has(k)) continue;
    seen.add(k);

    const key = `${translation}::${k}`;
    if (_preloadedKeys.has(key)) continue;
    _preloadedKeys.add(key);

    const task = () => {
      getChapter(parsed.book, parsed.chapter, translation).catch((err) => {
        _preloadedKeys.delete(key);
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[preload/ref] skipped', key, err?.message);
        }
      });
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(task, { timeout: 3000 });
    } else {
      setTimeout(task, 750);
    }
  }
};

// AI API calls
export const analyzeVerse = async (verse) => {
  try {
    const response = await axios.post(`${API}/ai/analyze`, {
      verse
    });
    return response.data;
  } catch (error) {
    console.error('Error analyzing verse:', error);
    throw error;
  }
};

export const aiSearch = async (query, translation = 'KJV') => {
  try {
    const response = await axios.post(`${API}/ai/search`, {
      query,
      translation
    });
    return response.data;
  } catch (error) {
    console.error('Error in AI search:', error);
    throw error;
  }
};

// History API calls
export const getSearchHistory = async (limit = 50) => {
  try {
    const response = await axios.get(`${API}/history`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching history:', error);
    throw error;
  }
};
